import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Sparkles, Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { addToWaitlist } from '@/services/waitlistService'

interface WaitlistSignupProps {
  variant?: 'hero' | 'cta'
}

/**
 * Get UTM parameters from current URL
 */
function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
  const utm: Record<string, string> = {}

  utmKeys.forEach((key) => {
    const value = params.get(key)
    if (value) utm[key] = value
  })

  return utm
}

export function WaitlistSignup({ variant = 'hero' }: WaitlistSignupProps) {
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('') // Bot trap - should remain empty
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot check - if filled, silently "succeed" but don't store
    if (honeypot) {
      setStatus('success')
      setEmail('')
      return
    }

    if (!email || !email.includes('@')) {
      setStatus('error')
      setErrorMessage('Please enter a valid email address')
      return
    }

    setStatus('loading')

    // Collect tracking data
    const utmParams = getUtmParams()
    const result = await addToWaitlist(email, {
      ...utmParams,
      referrer: document.referrer || undefined,
      landing_path: window.location.pathname,
    })

    if (result.success) {
      setStatus('success')
      setEmail('')
    } else {
      setStatus('error')
      setErrorMessage(result.error || 'Something went wrong')
    }
  }

  const isHero = variant === 'hero'

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex items-center justify-center gap-3 ${
          isHero
            ? 'text-[var(--color-kids-success)]'
            : 'text-white'
        }`}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isHero
            ? 'bg-[var(--color-kids-success)]/20'
            : 'bg-white/20'
        }`}>
          <Check className="w-5 h-5" />
        </div>
        <div className="text-left">
          <p className="font-display font-bold">You're on the list!</p>
          <p className={`text-sm ${isHero ? 'text-[var(--color-kids-text-muted)]' : 'text-white/80'}`}>
            We'll let you know when we launch.
          </p>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        {/* Honeypot field - hidden from humans, bots often fill it */}
        <input
          type="text"
          name="company"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] opacity-0 h-0 w-0"
        />
        <div className="relative flex-1">
          <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
            isHero ? 'text-[var(--color-kids-text-muted)]' : 'text-[var(--color-kids-primary)]'
          }`} />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (status === 'error') setStatus('idle')
            }}
            placeholder="Enter your email"
            className={`w-full pl-12 pr-4 py-3 rounded-[var(--radius-kid)] font-body text-[var(--font-size-kid-base)] transition-shadow focus:outline-none focus:ring-2 ${
              isHero
                ? 'bg-white border-2 border-[var(--color-kids-primary)]/20 text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:ring-[var(--color-kids-primary)]/30'
                : 'bg-white text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:ring-white/50'
            }`}
          />
        </div>
        <Button
          type="submit"
          size="lg"
          variant={isHero ? 'primary' : 'secondary'}
          isLoading={status === 'loading'}
          className="whitespace-nowrap"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Notify Me
        </Button>
      </form>

      <AnimatePresence>
        {status === 'error' && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`text-sm mt-2 ${
              isHero ? 'text-[var(--color-kids-danger)]' : 'text-white'
            }`}
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>

      <p className={`text-sm mt-3 ${
        isHero ? 'text-[var(--color-kids-text-muted)]' : 'text-white/70'
      }`}>
        Be the first to know when Inkwell Jr. launches.
      </p>
    </div>
  )
}
